import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Upper bound on how long we wait for this modal's closing animation before giving up on the
 * VIEW CART → drawer handoff. Generous on purpose: it is a safety net, not a timing knob —
 * the close normally settles in `--animation-speed` (~200-300ms).
 */
const LD_XSELL_CLOSE_TIMEOUT_MS = 1000;

/**
 * PDP post-add-to-cart cross-sell modal.
 *
 * Rendered by `sections/ld-atc-crosssell-modal.liquid`, and only when the product carries
 * bulbs in `ldd.compatible_products` — so on bulb-less products this element never exists
 * and add-to-cart behaves exactly as before.
 *
 * ── How the drawer gets suppressed (no substrate edits) ───────────────────────────────
 * `product-form.js` dispatches `CartAddEvent` (event name `cart:update`, `bubbles: true`)
 * from the `<product-form-component>` element on every add. `cart-drawer.js` listens for it
 * on `document` in the BUBBLE phase and calls `showDialog()` if the drawer element still has
 * the `auto-open` attribute at handle time. We listen on `document` in the CAPTURE phase, so
 * for any element-targeted dispatch we always run first: we strip `auto-open`, and restore it
 * in a `queueMicrotask` (the dispatch is synchronous, so every listener for this add has run
 * by the time the microtask fires). The drawer stays shut for exactly one add.
 *
 * We deliberately DO NOT stop propagation: the cart badge (`cart-icon.js`), the cart-section
 * hydration morph (`component-cart-items.js` — see PR #28), the sticky-ATC button reset
 * (`sticky-add-to-cart.js`) and the quick-add cache bust all depend on this event.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 * @property {HTMLElement} [confirmTitle] - Title slot in the "…has been added to your cart" bar.
 * @property {HTMLImageElement} [confirmImage] - Thumbnail slot in that same bar. Optional: the
 *   section renders no <img> at all when the product has no featured media.
 * @property {HTMLElement} [rail] - The horizontally scrolling card list (n>=3 only in practice).
 * @property {HTMLElement} [nav] - The scroll-next arrow. Only rendered from 3 cards up.
 *
 * @extends {DialogComponent}
 */
class LdAtcCrosssellModal extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd, { capture: true });
    // `scroll` does not bubble, but it does capture — so listening on the HOST catches the
    // rail's scrolls, including after a combined-listing morph swaps the rail node out from
    // under us (the host instance itself is reused, `refs` re-queries the live DOM).
    this.addEventListener('scroll', this.#syncNav, { capture: true });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd, { capture: true });
    this.removeEventListener('scroll', this.#syncNav, { capture: true });
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  /**
   * Handles a cart add. Opens this modal (instead of the drawer) for the main product, and
   * keeps it open — refreshing the confirmation bar — for adds made from inside the modal.
   *
   * @param {CustomEvent<{ data?: { source?: string, didError?: boolean, productId?: string } }>} event
   */
  #handleCartAdd = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    // Only product-form adds; and never touch the error path — `cart-drawer.js` has no
    // didError guard today, so bailing here keeps failed adds byte-identical to before.
    if (data.source !== 'product-form-component') return;
    if (data.didError) return;

    const target = event.target instanceof Node ? event.target : null;

    // Containment is checked BEFORE the product id so a product that lists itself among its
    // own `compatible_products` still reads as a bulb add (modal stays open) rather than a
    // main add (which would re-open the modal on top of itself).
    const isBulbAdd = target != null && this.contains(target);

    const mainProductId = this.dataset.productId;
    const isMainAdd = !isBulbAdd && Boolean(mainProductId) && String(data.productId) === String(mainProductId);

    // Anything else (Compatible-With rail, quick-add, quick-order-list, recommendations)
    // is left alone: the drawer opens for those exactly as it does today.
    if (!isBulbAdd && !isMainAdd) return;

    this.#suppressDrawerAutoOpenOnce();

    if (isBulbAdd) {
      const card = target instanceof Element ? target.closest('[data-ld-xsell-card]') : null;
      this.#setConfirmTitle(card instanceof HTMLElement ? card.dataset.productTitle : undefined);
      this.#setConfirmImage(card instanceof HTMLElement ? card.querySelector('img') : null);
      return;
    }

    this.#setConfirmTitle(this.dataset.productTitle);
    // Restore the main product's thumbnail: the confirmation bar may still be showing a bulb
    // from an earlier add in this same page view.
    this.#setConfirmImage(this.dataset.productImage);
    // No-ops when already open, which makes fast double-adds safe.
    this.showDialog();
  };

  /**
   * Momentarily removes `auto-open` from every cart drawer so the bubble-phase handler in
   * `cart-drawer.js` skips this one add, then restores it on the next microtask.
   */
  #suppressDrawerAutoOpenOnce() {
    for (const drawer of document.querySelectorAll('cart-drawer-component[auto-open]')) {
      drawer.removeAttribute('auto-open');
      queueMicrotask(() => drawer.setAttribute('auto-open', ''));
    }
  }

  /**
   * @param {string | undefined} title
   */
  #setConfirmTitle(title) {
    const confirmTitle = /** @type {HTMLElement | undefined} */ (this.refs.confirmTitle);
    if (!confirmTitle || !title) return;
    confirmTitle.textContent = title;
  }

  /**
   * Swaps the confirmation thumbnail. Accepts either a source `<img>` (a bulb card's image —
   * `currentSrc` so we reuse whatever the browser already picked from its srcset) or a plain
   * URL string (the main product, stamped on the host as `data-product-image`).
   *
   * Everything here is optional by design: `confirmImage` is absent when the product has no
   * featured media, and a bulb card can legitimately have no image either.
   *
   * @param {HTMLImageElement | Element | string | null | undefined} source
   */
  #setConfirmImage(source) {
    const confirmImage = /** @type {HTMLImageElement | undefined} */ (this.refs.confirmImage);
    if (!(confirmImage instanceof HTMLImageElement)) return;

    const src =
      typeof source === 'string' ? source : source instanceof HTMLImageElement ? source.currentSrc || source.src : '';
    if (!src) return;

    // The slot is rendered without a srcset on purpose, so setting `src` is enough — but clear
    // any that a future change might add, otherwise it would keep winning over `src`.
    confirmImage.removeAttribute('srcset');
    confirmImage.src = src;
    // Decorative: the adjacent text already names the product.
    confirmImage.alt = '';
  }

  /**
   * Scroll arrow. Advances the card rail by exactly one column — measured from the first two
   * cards so it stays correct across the n>=3 breakpoint widths (218px + 49px desktop,
   * 170px + 0 mobile) without duplicating those numbers here.
   */
  scrollNext() {
    const rail = /** @type {HTMLElement | undefined} */ (this.refs.rail);
    if (!rail) return;

    const cards = rail.querySelectorAll('[data-ld-xsell-card]');
    const first = cards[0];
    const second = cards[1];
    if (!(first instanceof HTMLElement)) return;

    const step =
      second instanceof HTMLElement ? second.offsetLeft - first.offsetLeft : first.getBoundingClientRect().width;
    if (!step) return;

    rail.scrollBy({ left: step, behavior: 'smooth' });
  }

  /**
   * VIEW CART → hand off to the mini-cart drawer instead of navigating to the cart page.
   *
   * ── Why this is sequential and not just "close and open" ──────────────────────────────
   * `dialog.js` scroll-locks by writing `body.style.{width,position,top}` directly, with no
   * reference counting, and `closeDialog()` only restores them AFTER its closing animation
   * settles — then calls `window.scrollTo(previousScrollY)`. So if the drawer opens while
   * this modal is still closing:
   *   1. the drawer reads `window.scrollY` through a `position: fixed` body and stores 0;
   *   2. our close finishes a few hundred ms later and clears the lock the drawer is relying
   *      on — the page scrolls behind an open drawer, and closing it jumps to the top.
   * That is the same failure as the PR #28 add-to-cart freeze. So: fully close first (which
   * releases the lock and restores scroll), and only then open the drawer, so exactly one
   * component owns the body styles at any instant.
   *
   * @param {MouseEvent} event
   */
  openCartDrawer = async (event) => {
    // Capture up front: after the first `await` the proxied event and the DOM may be stale.
    const link = event.target instanceof HTMLAnchorElement ? event.target : null;
    const href = link?.href;

    // Let the browser own modified / non-primary clicks (open in new tab, etc.).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    const drawer = document.querySelector('cart-drawer-component');
    // No drawer on the page (or an older one without the public API) → let the link navigate.
    if (!(drawer instanceof HTMLElement) || typeof (/** @type {any} */ (drawer).open) !== 'function') return;

    event.preventDefault();

    if (!(await this.#closeDialogWithTimeout())) {
      // The close never completed, so we cannot prove the scroll-lock was released. Opening a
      // second dialog now is exactly what strands `body { position: fixed }` — go to the cart
      // page instead, which is where this link pointed anyway.
      if (href) window.location.assign(href);
      return;
    }

    /** @type {any} */ (drawer).open();
  };

  /**
   * Closes the dialog, but never waits forever. `closeDialog()` awaits `onAnimationEnd`,
   * which resolves off `getAnimations()` — an empty list settles immediately, so a *missing*
   * closing animation is already safe. An infinite or paused one would not be, hence the cap.
   *
   * @returns {Promise<boolean>} whether the close fully completed. `closeDialog()` flips
   *   `dialog.open` only after it has restored the body styles and scroll position, so this
   *   is a reliable "the lock is released" signal.
   */
  async #closeDialogWithTimeout() {
    const { dialog } = this.refs;
    if (!dialog?.open) return true;

    await Promise.race([
      this.closeDialog(),
      new Promise((resolve) => setTimeout(resolve, LD_XSELL_CLOSE_TIMEOUT_MS)),
    ]);

    return !dialog.open;
  }

  /**
   * Hides the scroll arrow once the rail is scrolled to its end (and re-shows it if the
   * shopper scrolls back). Purely cosmetic — `scrollNext()` is already a no-op at the end.
   */
  #syncNav = () => {
    const rail = /** @type {HTMLElement | undefined} */ (this.refs.rail);
    const nav = /** @type {HTMLElement | undefined} */ (this.refs.nav);
    if (!rail || !(nav instanceof HTMLElement)) return;

    nav.toggleAttribute('hidden', Math.ceil(rail.scrollLeft + rail.clientWidth) >= rail.scrollWidth);
  };

  /** @param {CustomEvent<{ sectionId?: string }>} event */
  #handleSectionSelect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;
    this.showDialog();
  };

  /** @param {CustomEvent<{ sectionId?: string }>} event */
  #handleSectionDeselect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;
    this.closeDialog();
  };
}

if (!customElements.get('ld-atc-crosssell-modal')) {
  customElements.define('ld-atc-crosssell-modal', LdAtcCrosssellModal);
}
